// Imported by vite.config.ts (prerender slugs), so: no imports.
// Copy source: docs/design/landing/copy.md section 2.12 and the web-landing-glossary design.
// Definitions are our own wording of the ATO / business.gov.au page named in `sources`
// (no text copied from competitors). Re-open every source and bump `checked` when you edit.

export interface GlossarySource {
  label: string
  href: string
}

export interface GlossaryEntry {
  slug: string
  term: string
  /** Page H1 and title, e.g. "What is an odometer?". */
  question: string
  /** One line; shown on the index and as the answer box on the entry page. */
  definition: string
  /** 120-155 characters, for the page meta description. */
  metaDescription: string
  /** Optional second block on the entry page. */
  body?: { heading: string; text: string }
  sources: [GlossarySource, ...GlossarySource[]]
  /** ISO date the sources were last opened and checked. */
  checked: string
  /** Slugs of related entries. */
  related: string[]
}

const ATO = "https://www.ato.gov.au/businesses-and-organisations"
const MOTOR = `${ATO}/income-deductions-and-concessions/income-and-deductions-for-business/deductions/deductions-for-motor-vehicle-expenses`

const LOGBOOK_SRC = { label: "ATO, logbook method", href: `${MOTOR}/logbook-method` }
const CENTS_SRC = {
  label: "ATO, cents per kilometre method",
  href: `${MOTOR}/cents-per-kilometre-method`,
}
const GST_SRC = {
  label: "ATO, how GST works",
  href: `${ATO}/gst-excise-and-indirect-taxes/gst/how-gst-works`,
}
const CHECKED = "2026-10-28"

export const GLOSSARY_ENTRIES: readonly GlossaryEntry[] = [
  {
    slug: "abn",
    term: "ABN",
    question: "What is an ABN?",
    definition: "An Australian Business Number: an 11-digit number that identifies your business.",
    metaDescription:
      "An ABN is an 11-digit Australian Business Number that identifies your business. See what it is, who gets one and where you use it, with the source.",
    body: {
      heading: "Where it is used",
      text: "A sole trader is entitled to an ABN and uses it for all business activities.",
    },
    sources: [
      {
        label: "business.gov.au, register for an ABN",
        href: "https://business.gov.au/registrations/register-for-an-australian-business-number-abn",
      },
      {
        label: "ATO, business structures: key tax obligations",
        href: `${ATO}/starting-registering-or-closing-a-business/starting-your-own-business/business-structures-key-tax-obligations`,
      },
    ],
    checked: CHECKED,
    related: ["gst", "sole-trader", "tax-invoice"],
  },
  {
    slug: "bas",
    term: "BAS",
    question: "What is a BAS?",
    definition:
      "Business activity statement: the form you use to report and pay GST, and some other taxes such as PAYG instalments, to the ATO.",
    metaDescription:
      "A BAS is the business activity statement you use to report and pay GST and some other taxes to the ATO. Plain-English definition with the ATO source.",
    sources: [
      {
        label: "ATO, business activity statements (BAS)",
        href: `${ATO}/preparing-lodging-and-paying/business-activity-statements-bas`,
      },
    ],
    checked: CHECKED,
    related: ["gst", "abn"],
  },
  {
    slug: "gst",
    term: "GST",
    question: "What is GST?",
    definition:
      "Goods and services tax: a tax of 10% on most goods, services and other items sold or consumed in Australia.",
    metaDescription:
      "GST is a 10% tax on most goods and services sold in Australia. See what it is and when you must register, in plain English with the ATO source linked.",
    body: {
      heading: "When you must register",
      text: "You need to register for GST if you run a business and your GST turnover is $75,000 or more, or if you provide taxi travel, which includes ride-sourcing.",
    },
    sources: [GST_SRC],
    checked: CHECKED,
    related: ["abn", "bas", "tax-invoice"],
  },
  {
    slug: "sole-trader",
    term: "Sole trader",
    question: "What is a sole trader?",
    definition:
      "An individual running a business: you are its sole owner and controller and legally responsible for its debts and losses.",
    metaDescription:
      "A sole trader is an individual running a business in their own name. See what that means for tax, your ABN and liability, with the ATO source linked.",
    body: {
      heading: "Tax",
      text: "A sole trader reports business income in their own tax return. There is no separate business tax return.",
    },
    sources: [
      {
        label: "ATO, business structures: key tax obligations",
        href: `${ATO}/starting-registering-or-closing-a-business/starting-your-own-business/business-structures-key-tax-obligations`,
      },
    ],
    checked: CHECKED,
    related: ["abn", "cents-per-kilometre", "logbook"],
  },
  {
    slug: "tax-invoice",
    term: "Tax invoice",
    question: "What is a tax invoice?",
    definition:
      "A document that shows the seller's ABN and the GST in a sale; if a customer asks for one you must provide it within 28 days, unless the sale is $82.50 or less including GST.",
    metaDescription:
      "A tax invoice shows the seller's ABN and the GST in a sale. See when you must provide one and the $82.50 rule, with the ATO source and a checked date.",
    sources: [
      { label: "ATO, tax invoices", href: `${ATO}/gst-excise-and-indirect-taxes/gst/tax-invoices` },
    ],
    checked: CHECKED,
    related: ["abn", "gst"],
  },
  {
    slug: "cents-per-kilometre",
    term: "Cents per kilometre",
    question: "What is the cents per kilometre method?",
    definition:
      "An ATO method for car expenses: business kilometres (up to 5,000 per car a year) multiplied by a set rate for the income year.",
    metaDescription:
      "Cents per kilometre is an ATO method for car expenses: business km up to 5,000 per car times a set rate. Plain-English definition with the ATO source.",
    body: {
      heading: "Who can use it",
      text: "Only sole traders and partnerships (where at least one partner is an individual) claiming for a car. The rate already takes running costs and depreciation into account.",
    },
    sources: [
      CENTS_SRC,
      {
        label: "ATO, motor vehicle expense calculation methods",
        href: `${MOTOR}/motor-vehicle-expense-calculation-methods`,
      },
    ],
    checked: CHECKED,
    related: ["logbook", "decline-in-value", "sole-trader"],
  },
  {
    slug: "decline-in-value",
    term: "Decline in value",
    question: "What is decline in value?",
    definition:
      "Depreciation: the way the cost of an asset, such as a car, is claimed over time as its value falls.",
    metaDescription:
      "Decline in value is depreciation: claiming the cost of an asset such as a car over time. See how it applies to the logbook method, with the ATO source.",
    body: {
      heading: "With a car",
      text: "If you use the logbook method you can generally claim the decline in value of the car, on the business portion of its cost. The cents per kilometre rate already includes it.",
    },
    sources: [
      LOGBOOK_SRC,
      {
        label: "ATO, depreciation and capital expenses and allowances",
        href: `${ATO}/income-deductions-and-concessions/depreciation-and-capital-expenses-and-allowances`,
      },
    ],
    checked: CHECKED,
    related: ["logbook", "cents-per-kilometre", "business-use-percentage"],
  },
  {
    slug: "logbook",
    term: "Logbook",
    question: "What is a logbook?",
    definition:
      "A record of your car journeys used to work out how much of the car's use is for business; the first year you use it, keep it for at least 12 continuous weeks.",
    metaDescription:
      "A logbook records your car journeys to work out business use. See the 12 continuous weeks rule and what it must hold, in plain English with the ATO source.",
    body: {
      heading: "What it includes",
      text: "Among other things: the start and end of the logbook period, odometer readings at the start and end, total kilometres, the business-use percentage, the car's make, model, engine capacity and registration, and for each journey its reason, dates, odometer readings and kilometres. The 12 weeks must be representative of your travel through the year, and a logbook is valid for five years.",
    },
    sources: [LOGBOOK_SRC],
    checked: CHECKED,
    related: ["odometer", "business-use-percentage", "cents-per-kilometre"],
  },
  {
    slug: "odometer",
    term: "Odometer",
    question: "What is an odometer?",
    definition:
      "The instrument in a vehicle that shows the total distance it has travelled; the difference between two readings is the kilometres driven in between.",
    metaDescription:
      "An odometer shows a vehicle's total distance travelled. See why the ATO logbook method asks for odometer readings and what to record, with the source.",
    body: {
      heading: "In your logbook",
      text: "The ATO logbook method asks for the car's odometer readings at the start and end of the logbook period and of each journey.",
    },
    sources: [LOGBOOK_SRC],
    checked: CHECKED,
    related: ["logbook", "cents-per-kilometre", "business-use-percentage"],
  },
  {
    slug: "business-use-percentage",
    term: "Business-use percentage",
    question: "What is the business-use percentage?",
    definition:
      "The share of a car's use that is for business: business kilometres divided by total kilometres, times 100.",
    metaDescription:
      "Business-use percentage is business km divided by total km, times 100. See how the logbook method uses it to work out your car claim, with the ATO source.",
    body: {
      heading: "How it is used",
      text: "With the logbook method you multiply your total car expenses for the income year by this percentage to work out what you can claim.",
    },
    sources: [LOGBOOK_SRC],
    checked: CHECKED,
    related: ["logbook", "odometer", "decline-in-value"],
  },
]

export const GLOSSARY_GROUPS: readonly { title: string; slugs: string[] }[] = [
  { title: "Business, GST and BAS", slugs: ["abn", "bas", "gst", "sole-trader", "tax-invoice"] },
  {
    title: "Car and logbook",
    slugs: [
      "cents-per-kilometre",
      "decline-in-value",
      "logbook",
      "odometer",
      "business-use-percentage",
    ],
  },
]

export const getGlossaryEntry = (slug: string): GlossaryEntry | undefined =>
  GLOSSARY_ENTRIES.find((e) => e.slug === slug)
